import { useCallback, useEffect, useRef, useState } from 'react'
import { Send } from 'lucide-react'
import { Note } from '@/shared/ui/states'
import type { MeetingMessage } from './types'

/**
 * The chat and the notepad, written once against a pair of callbacks so the staff page and
 * the guest page share them.
 *
 * The two screens hold different credentials — a staff session and a guest session, on
 * different filter chains — so the panels take the functions rather than importing a client.
 * That is also what stops a guest page accidentally calling a staff route.
 */

const CHAT_POLL_MS = 4000

export interface ChatIo {
  list: (since?: string) => Promise<MeetingMessage[]>
  post: (body: string) => Promise<MeetingMessage>
}

/**
 * The meeting chat.
 *
 * ## Why it polls and does not stream
 *
 * Nothing in this product pushes. The alternative here was the media server's data channel,
 * which is already carrying the video — and it is refused on purpose: a chat that arrives
 * over WebRTC leaves no row, so "what was said, and by whom" has no answer the day it is
 * asked. Four seconds of latency buys a record.
 *
 * ## Why it only ever asks for what it does not have
 *
 * `since` is the last line held. Re-reading the thread every poll would mean a long meeting
 * re-sending itself to everybody in it every four seconds.
 */
export function ChatPanel({ io, disabled }: { io: ChatIo; disabled?: boolean }) {
  const [messages, setMessages] = useState<MeetingMessage[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bottom = useRef<HTMLDivElement | null>(null)
  // A ref, not state: the poll reads it without re-subscribing on every new line.
  const cursor = useRef<string | undefined>(undefined)

  const pull = useCallback(async () => {
    try {
      const incoming = await io.list(cursor.current)
      const last = incoming[incoming.length - 1]
      if (!last) return
      cursor.current = last.at
      setMessages((held) => {
        const seen = new Set(held.map((m) => m.id))
        return [...held, ...incoming.filter((m) => !seen.has(m.id))]
      })
    } catch (e) {
      setError((e as Error).message)
    }
  }, [io])

  useEffect(() => {
    void pull()
    const t = setInterval(() => { void pull() }, CHAT_POLL_MS)
    return () => clearInterval(t)
  }, [pull])

  // Only when a line arrives, and only to the end: scrolling on every render would fight
  // somebody reading back through what was said earlier.
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }) }, [messages.length])

  const send = async () => {
    const body = draft.trim()
    if (!body) return
    setSending(true)
    setError(null)
    try {
      await io.post(body)
      setDraft('')
      await pull()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
        {messages.length === 0 ? (
          <p className="py-6 text-center text-erp-xs text-muted-foreground">
            Nothing said yet. What is written here is kept with the meeting.
          </p>
        ) : messages.map((message) => (
          <div key={message.id} className="text-erp-sm">
            <span className="font-medium">{message.authorName ?? 'Someone'}</span>
            {message.guest && (
              <span className="ml-1 text-erp-xs text-muted-foreground">(guest)</span>
            )}
            <span className="ml-2 text-erp-xs text-muted-foreground">
              {new Date(message.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <p className="whitespace-pre-wrap break-words">{message.body}</p>
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {error && <p className="mt-2 text-erp-xs text-destructive">{error}</p>}

      <div className="mt-2 flex items-end gap-2">
        <textarea
          className="erp-input min-h-9 w-full resize-none py-1.5 text-erp-sm"
          rows={2}
          value={draft}
          disabled={disabled || sending}
          placeholder={disabled ? 'You are not in this meeting' : 'Write to the meeting…'}
          onChange={(e) => setDraft(e.target.value)}
          // Enter sends, Shift+Enter breaks the line: the shape everybody already expects
          // from every other chat they use.
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
        />
        <button type="button" className="erp-btn erp-btn-primary h-9 shrink-0 text-erp-sm"
          disabled={disabled || sending || !draft.trim()} onClick={() => void send()}>
          <Send className="size-4" />
        </button>
      </div>
    </div>
  )
}

export interface NoteIo {
  load: () => Promise<{ body: string }>
  save: (body: string) => Promise<unknown>
}

/**
 * A private notepad, one per person per meeting.
 *
 * ## Saved on a pause, not on a button
 *
 * Notes taken during a meeting are taken by somebody who is listening, and a Save button is
 * the thing they forget. It writes a second after typing stops, and says plainly when it last
 * wrote — because a notepad that claims to save silently is a notepad people stop trusting.
 */
export function NotesPanel({ io }: { io: NoteIo }) {
  const [body, setBody] = useState('')
  const [state, setState] = useState<'loading' | 'idle' | 'saving' | 'saved' | 'failed'>('loading')
  const dirty = useRef(false)

  useEffect(() => {
    io.load()
      .then((note) => { setBody(note.body ?? ''); setState('idle') })
      .catch(() => setState('failed'))
  }, [io])

  useEffect(() => {
    if (!dirty.current) return
    const t = setTimeout(() => {
      setState('saving')
      io.save(body)
        .then(() => setState('saved'))
        .catch(() => setState('failed'))
    }, 1000)
    return () => clearTimeout(t)
  }, [body, io])

  // The tab can be closed mid-sentence; a last write on the way out costs nothing.
  useEffect(() => {
    const flush = () => { if (dirty.current) void io.save(body) }
    window.addEventListener('pagehide', flush)
    return () => { window.removeEventListener('pagehide', flush); flush() }
  }, [body, io])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Note tone="info">
        Private to you. The chair cannot read these, and nothing here is shared with the
        meeting.
      </Note>
      <textarea
        className="erp-input mt-2 min-h-0 w-full flex-1 resize-none py-2 text-erp-sm"
        value={body}
        placeholder="Your notes…"
        onChange={(e) => { dirty.current = true; setBody(e.target.value) }}
      />
      <p className="mt-1 text-right text-erp-xs text-muted-foreground">
        {state === 'loading' ? 'Opening…'
          : state === 'saving' ? 'Saving…'
          : state === 'saved' ? 'Saved'
          : state === 'failed' ? 'Could not save — your text is still here; try again in a moment.'
          : 'Saves as you type'}
      </p>
    </div>
  )
}
